import React, { useEffect, useState } from 'react';
import { Temperature, Wifi, Location } from '@carbon/icons-react';
import { AggregatedDataService } from '../../services/AggregatedDataService';
import { supabase } from '../../services/supabaseClient';
import { formatTemperature, formatHumidity } from '../../lib/timeUtils';
import mapBoxGeocodingService from '../../services/MapBoxGeocodingService';
import TemperatureSparkline from '../charts/TemperatureSparkline';

const addressCache = new Map();

async function fetchSparklineValues(sensorId, preferTrip = false, timeRange = null) {
  const end = timeRange?.to ? new Date(timeRange.to) : new Date();
  const start = timeRange?.from
    ? new Date(timeRange.from)
    : new Date(end.getTime() - 6 * 60 * 60 * 1000);

  if (preferTrip) {
    const { data: tripRows } = await supabase
      .from('gps_tracks')
      .select('temperature, recorded_at, latitude, longitude')
      .eq('sensor_id', sensorId)
      .gte('recorded_at', start.toISOString())
      .lte('recorded_at', end.toISOString())
      .order('recorded_at', { ascending: true })
      .limit(120);

    const tripPoints = (tripRows || [])
      .filter((row) => row.temperature != null)
      .map((row) => ({
        value: Number(row.temperature),
        timestamp: row.recorded_at,
        lat: row.latitude == null ? null : Number(row.latitude),
        lng: row.longitude == null ? null : Number(row.longitude),
      }));

    if (tripPoints.length >= 2) return tripPoints;
  }

  const chartData = await AggregatedDataService.fetchChartData(sensorId, 'HOUR', start, end);
  const readings = chartData?.readings || [];
  const points = readings
    .filter((r) => r.temperature != null)
    .map((r) => ({
      value: Number(r.temperature),
      timestamp: r.timestamp instanceof Date ? r.timestamp.toISOString() : r.timestamp,
      lat: null,
      lng: null,
    }));

  return points.length >= 2 ? points : [];
}

async function findNearestGpsPoint(sensorId, timestamp) {
  if (!sensorId || !timestamp) return null;
  const iso = new Date(timestamp).toISOString();
  const fields = 'latitude, longitude, recorded_at, temperature';

  const [beforeResult, afterResult] = await Promise.all([
    supabase
      .from('gps_tracks')
      .select(fields)
      .eq('sensor_id', sensorId)
      .lte('recorded_at', iso)
      .order('recorded_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from('gps_tracks')
      .select(fields)
      .eq('sensor_id', sensorId)
      .gte('recorded_at', iso)
      .order('recorded_at', { ascending: true })
      .limit(1)
      .maybeSingle(),
  ]);

  const candidates = [beforeResult.data, afterResult.data]
    .filter((row) => row?.latitude != null && row?.longitude != null);
  if (!candidates.length) return null;

  const targetTime = new Date(timestamp).getTime();
  const nearest = candidates.reduce((best, row) => (
    Math.abs(new Date(row.recorded_at).getTime() - targetTime)
      < Math.abs(new Date(best.recorded_at).getTime() - targetTime)
      ? row
      : best
  ));
  const positionTime = new Date(nearest.recorded_at).getTime();
  if (Math.abs(positionTime - targetTime) > 30 * 60 * 1000) return null;

  return {
    lat: Number(nearest.latitude),
    lng: Number(nearest.longitude),
    recordedAt: nearest.recorded_at,
  };
}

async function fetchZoneSparklines(zone, timeRange = null) {
  const sensorIds = zone.sensor_ids || [];
  if (sensorIds.length === 0) return { tripValues: [], deviceValues: [] };

  const end = timeRange?.to ? new Date(timeRange.to) : new Date();
  const tripStart = timeRange?.from
    ? new Date(timeRange.from)
    : new Date(end.getTime() - 6 * 60 * 60 * 1000);
  const deviceStart = timeRange?.from
    ? new Date(timeRange.from)
    : new Date(end.getTime() - 60 * 60 * 1000);

  const { data: tripRows } = await supabase
    .from('gps_tracks')
    .select('temperature, recorded_at, sensor_id')
    .in('sensor_id', sensorIds)
    .gte('recorded_at', tripStart.toISOString())
    .lte('recorded_at', end.toISOString())
    .order('recorded_at', { ascending: true })
    .limit(200);

  const tripTemps = (tripRows || [])
    .filter((row) => row.temperature != null)
    .map((row) => Number(row.temperature));

  const deviceResults = await Promise.all(
    sensorIds.map(async (sid) => {
      try {
        const chartData = await AggregatedDataService.fetchChartData(sid, 'HOUR', deviceStart, end);
        const readings = chartData?.readings || [];
        return readings
          .filter((r) => r.temperature != null)
          .map((r) => Number(r.temperature));
      } catch {
        return [];
      }
    })
  );

  const deviceTemps = deviceResults.flat();

  return {
    tripValues: tripTemps.length >= 2 ? tripTemps : [],
    deviceValues: deviceTemps.length >= 2 ? deviceTemps : [],
  };
}

export default function SensorMapPopup({
  marker,
  isDark,
  timeRange = null,
  onSparklinePointSelect,
}) {
  const [sparklinePoints, setSparklinePoints] = useState([]);
  const [sparklineLoading, setSparklineLoading] = useState(marker.type === 'sensor');
  const [pointLocationStatus, setPointLocationStatus] = useState('');

  const [zoneTripValues, setZoneTripValues] = useState([]);
  const [zoneDeviceValues, setZoneDeviceValues] = useState([]);
  const [zoneLoading, setZoneLoading] = useState(marker.type === 'zone');
  const [address, setAddress] = useState('');
  const [addressLoading, setAddressLoading] = useState(true);

  const sensorId = marker.type === 'sensor' ? marker.meta?.id : null;
  const zoneMeta = marker.type === 'zone' ? marker.meta : null;

  useEffect(() => {
    if (!sensorId) return undefined;

    let cancelled = false;
    setSparklineLoading(true);

    fetchSparklineValues(sensorId, marker.readingSource === 'trip', timeRange)
      .then((points) => {
        if (!cancelled) setSparklinePoints(points);
      })
      .catch(() => {
        if (!cancelled) setSparklinePoints([]);
      })
      .finally(() => {
        if (!cancelled) setSparklineLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sensorId, marker.readingSource, timeRange]);

  useEffect(() => {
    if (!zoneMeta) return undefined;

    let cancelled = false;
    setZoneLoading(true);

    fetchZoneSparklines(zoneMeta, timeRange)
      .then(({ tripValues, deviceValues }) => {
        if (!cancelled) {
          setZoneTripValues(tripValues);
          setZoneDeviceValues(deviceValues);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setZoneTripValues([]);
          setZoneDeviceValues([]);
        }
      })
      .finally(() => {
        if (!cancelled) setZoneLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [zoneMeta, timeRange]);

  useEffect(() => {
    const cacheKey = `${marker.lat.toFixed(5)},${marker.lng.toFixed(5)}`;
    const cached = addressCache.get(cacheKey);
    if (cached) {
      setAddress(cached);
      setAddressLoading(false);
      return undefined;
    }

    let cancelled = false;
    setAddress('');
    setAddressLoading(true);

    mapBoxGeocodingService.reverseGeocode(marker.lng, marker.lat)
      .then((result) => {
        if (cancelled) return;
        const resolved = result?.formattedAddress || result?.streetAddress || 'Address unavailable';
        addressCache.set(cacheKey, resolved);
        setAddress(resolved);
      })
      .catch(() => {
        if (!cancelled) setAddress('Address unavailable');
      })
      .finally(() => {
        if (!cancelled) setAddressLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [marker.lat, marker.lng]);

  const handleSparklinePointClick = async ({ index, value }) => {
    const point = sparklinePoints[index];
    if (!point || !onSparklinePointSelect) return;

    setPointLocationStatus('Locating GPS point…');
    try {
      const location = point.lat != null && point.lng != null
        ? { lat: point.lat, lng: point.lng, recordedAt: point.timestamp }
        : await findNearestGpsPoint(sensorId, point.timestamp);

      if (!location) {
        setPointLocationStatus('No GPS position was recorded near this reading.');
        return;
      }

      onSparklinePointSelect({
        ...location,
        sensorId,
        sensorName: marker.name,
        temperature: value,
        readingAt: point.timestamp,
      });
      setPointLocationStatus(
        `Showing GPS point at ${new Date(location.recordedAt).toLocaleString()}`,
      );
    } catch {
      setPointLocationStatus('Unable to locate this reading on the map.');
    }
  };

  return (
    <div style={{ minWidth: 200 }}>
      <strong>{marker.name}</strong>
      <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
        {marker.type === 'zone' && <><Location size={12} /> Zone</>}
        {marker.type === 'sensor' && <><Temperature size={12} /> Sensor</>}
        {marker.type === 'gateway' && <><Wifi size={12} /> Gateway</>}
      </div>

      {marker.type === 'sensor' && (
        <>
          <div style={{ fontSize: '0.75rem', marginTop: 6 }}>
            <div>
              <Temperature size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {formatTemperature(marker.temperature, marker.temperatureUnit)}
              {marker.humidity != null && (
                <span style={{ marginLeft: 8, opacity: 0.85 }}>
                  {formatHumidity(marker.humidity)}
                </span>
              )}
            </div>
            {marker.readingAt && (
              <div style={{ fontSize: '0.7rem', marginTop: 2, opacity: 0.8 }}>
                Reading {new Date(marker.readingAt).toLocaleString()}
              </div>
            )}
          </div>
          <TemperatureSparkline
            values={sparklinePoints.map((point) => point.value)}
            loading={sparklineLoading}
            isDark={isDark}
            label={timeRange ? 'Selected range' : marker.readingSource === 'trip' ? 'Trip (6h)' : 'Last 6h'}
            onPointClick={handleSparklinePointClick}
          />
          {sparklinePoints.length >= 2 && (
            <div style={{ fontSize: '0.65rem', marginTop: 3, opacity: 0.8 }}>
              {pointLocationStatus || 'Click a chart point to show its GPS location.'}
            </div>
          )}
        </>
      )}

      {marker.type === 'zone' && (
        <>
          <TemperatureSparkline
            values={zoneTripValues}
            loading={zoneLoading}
            isDark={isDark}
            label={timeRange ? 'Trip — selected range' : 'Trip (6h)'}
            color="#ff6b35"
          />
          <TemperatureSparkline
            values={zoneDeviceValues}
            loading={zoneLoading}
            isDark={isDark}
            label={timeRange ? 'Device — selected range' : 'Device (1h)'}
            color="#6929c4"
          />
        </>
      )}

      <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
        {marker.lat.toFixed(5)}, {marker.lng.toFixed(5)}
      </div>
      <div style={{ fontSize: '0.7rem', marginTop: 3, opacity: 0.85, maxWidth: 250 }}>
        <Location size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        {addressLoading ? 'Resolving address…' : address}
      </div>
      {marker.recordedAt && (
        <div style={{ fontSize: '0.7rem', marginTop: 4, opacity: 0.8 }}>
          Position {new Date(marker.recordedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}
